import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SignalPanel.css';

const DEFAULT_PAIRS = [
    'BTCUSDT','ETHUSDT','SOLUSDT','BNBUSDT',
    'XRPUSDT','ADAUSDT','DOGEUSDT','AVAXUSDT',
    'LINKUSDT','DOTUSDT','MATICUSDT','ATOMUSDT',
    'LTCUSDT','UNIUSDT',
];

const STORAGE_KEY = 'signal_panel_pairs';
const loadPairs = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) return JSON.parse(saved);
    } catch (_) {}
    return DEFAULT_PAIRS;
};
const savePairs = (pairs) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pairs)); } catch (_) {}
};

const SIG_COLOR = {
    'STRONG BUY': '#00c851', 'BUY': '#4caf50',
    'HOLD': '#718096',
    'SELL': '#f44336', 'STRONG SELL': '#b71c1c'
};

const fmtPrice = (p) => {
    if (!p && p !== 0) return '—';
    if (p >= 1000) return '$' + p.toLocaleString(undefined, { maximumFractionDigits: 2 });
    if (p >= 1)    return '$' + p.toFixed(4);
    return '$' + p.toFixed(6);
};

const SignalPanel = ({ onApplyTrade, prices }) => {
    const [watchedPairs, setWatchedPairs] = useState(loadPairs);
    const [signals,  setSignals]  = useState({});
    const [loading,  setLoading]  = useState({});
    const [expanded, setExpanded] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [addInput,  setAddInput]  = useState('');
    const [editMode,  setEditMode]  = useState(false);
    const timer = useRef(null);

    const fetchSignal = useCallback(async (sym) => {
        setLoading(prev => ({ ...prev, [sym]: true }));
        try {
            const r = await fetch(`/api/analysis/${sym}`);
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            setSignals(prev => ({ ...prev, [sym]: data }));
        } catch (e) {
            console.error(`Signal fetch error ${sym}:`, e.message);
        } finally {
            setLoading(prev => ({ ...prev, [sym]: false }));
        }
    }, []);

    const fetchAll = useCallback(() => {
        watchedPairs.forEach(sym => fetchSignal(sym));
        setLastRefresh(new Date());
    }, [fetchSignal, watchedPairs]);

    useEffect(() => {
        fetchAll();
        timer.current = setInterval(fetchAll, 60_000);
        return () => clearInterval(timer.current);
    }, [fetchAll]);

    const addPair = () => {
        const sym = addInput.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
        const full = sym.endsWith('USDT') ? sym : sym + 'USDT';
        if (!full || watchedPairs.includes(full)) { setAddInput(''); return; }
        const updated = [...watchedPairs, full];
        setWatchedPairs(updated);
        savePairs(updated);
        fetchSignal(full);
        setAddInput('');
    };

    const removePair = (sym) => {
        const updated = watchedPairs.filter(p => p !== sym);
        setWatchedPairs(updated);
        savePairs(updated);
    };

    const resetToDefaults = () => {
        setWatchedPairs(DEFAULT_PAIRS);
        savePairs(DEFAULT_PAIRS);
        DEFAULT_PAIRS.forEach(sym => fetchSignal(sym));
    };

    const handleApply = (sym, data) => {
        if (!onApplyTrade) return;
        const base = sym.replace('USDT', '');
        const price = data.levels.entryZone?.[0] || 0;
        const sl    = data.levels.stopLoss;
        const tp    = data.levels.takeProfit;
        const slPct = price > 0 ? Math.abs((price - sl) / price * 100) : 3;
        const tpPct = price > 0 ? Math.abs((tp - price) / price * 100) : 6;
        onApplyTrade({
            pair:   base + 'USDT',
            action: data.signal.includes('BUY') ? 'BUY' : 'SELL',
            signal: data.signal,
            stopLossPercent:   parseFloat(slPct.toFixed(2)),
            takeProfitPercent: parseFloat(tpPct.toFixed(2))
        });
    };

    return (
        <div className="signal-panel">
            <div className="signal-panel-header">
                <div>
                    <h3>🎯 Smart Signals</h3>
                    <div className="sp-meta">
                        15m candles · 5 strategies · {watchedPairs.length} pairs ·{' '}
                        {lastRefresh ? `updated ${lastRefresh.toLocaleTimeString()}` : 'loading…'}
                    </div>
                </div>
                <div style={{display:'flex',gap:'6px'}}>
                    <button className="sp-refresh-btn" onClick={() => setEditMode(e => !e)} title="Edit watchlist">✏️</button>
                    <button className="sp-refresh-btn" onClick={fetchAll} title="Refresh now">↻</button>
                </div>
            </div>

            {/* Watchlist editor */}
            {editMode && (
                <div className="sp-editor">
                    <div className="sp-editor-add">
                        <input
                            className="sp-editor-input"
                            placeholder="Add pair, e.g. XMR or XMRUSDT"
                            value={addInput}
                            onChange={e => setAddInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addPair()}
                        />
                        <button className="sp-editor-btn" onClick={addPair}>＋ Add</button>
                        <button className="sp-editor-btn sp-editor-reset" onClick={resetToDefaults}>Reset</button>
                    </div>
                    <div className="sp-editor-chips">
                        {watchedPairs.map(sym => (
                            <span key={sym} className="sp-chip">
                                {sym.replace('USDT','')}
                                <button className="sp-chip-remove" onClick={() => removePair(sym)}>×</button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="sp-legend">
                <span className="sp-leg buy">■ BUY</span>
                <span className="sp-leg hold">■ HOLD</span>
                <span className="sp-leg sell">■ SELL</span>
            </div>

            <div className="signal-cards">
                {watchedPairs.map(sym => {
                    const base  = sym.replace('USDT', '');
                    const d     = signals[sym];
                    const isLoading = loading[sym] && !d;
                    const priceObj  = prices?.[sym];
                    const livePrice = priceObj
                        ? (typeof priceObj === 'object' ? priceObj.price : priceObj)
                        : null;
                    const color = d ? (SIG_COLOR[d.signal] || '#718096') : '#4a5568';

                    return (
                        <div key={sym} className="signal-card">
                            {/* Header */}
                            <div className="sc-hdr">
                                <span className="sc-pair"><b>{base}</b><small>/USDT</small></span>
                                <span className="sc-live-price">{fmtPrice(livePrice)}</span>
                                {editMode && (
                                    <button className="sc-remove-btn" onClick={() => removePair(sym)} title="Remove from watchlist">×</button>
                                )}
                            </div>

                            {isLoading && <div className="sc-status">Analysing…</div>}
                            {!isLoading && !d && <div className="sc-status sc-err">No data yet</div>}

                            {d && (<>
                                {/* Signal badge */}
                                <div className="sc-badge" style={{ background: color }}>
                                    {d.signal}
                                </div>

                                {/* Confidence bar */}
                                <div className="sc-conf-wrap">
                                    <div className="sc-conf-track">
                                        <div className="sc-conf-fill"
                                            style={{ width: `${d.confidence}%`, background: color }} />
                                    </div>
                                    <span className="sc-conf-pct">{d.confidence}%</span>
                                </div>

                                {/* Key levels */}
                                <div className="sc-levels">
                                    <span className="sc-sl">SL {fmtPrice(d.levels.stopLoss)}</span>
                                    <span className="sc-tp">TP {fmtPrice(d.levels.takeProfit)}</span>
                                </div>

                                {/* Strategy vote pills */}
                                <div className="sc-votes">
                                    {d.strategies.map(s => (
                                        <span key={s.name}
                                            className={`sc-pill sc-pill-${s.signal.replace(/\s+/g,'').toLowerCase()}`}
                                            title={`${s.name}: ${s.reason}`}>
                                            {s.name.split(' ').map(w => w[0]).join('')}
                                        </span>
                                    ))}
                                </div>

                                {/* Expand/collapse strategy breakdown */}
                                <button className="sc-expand-btn"
                                    onClick={() => setExpanded(expanded === sym ? null : sym)}>
                                    {expanded === sym ? '▲ Hide breakdown' : '▼ Strategy details'}
                                </button>

                                {expanded === sym && (
                                    <div className="sc-breakdown">
                                        {d.strategies.map(s => (
                                            <div key={s.name} className="sc-breakdown-row">
                                                <span className={`sc-pill sc-pill-${s.signal.replace(/\s+/g,'').toLowerCase()}`}>
                                                    {s.signal}
                                                </span>
                                                <div className="sc-breakdown-text">
                                                    <span className="sc-sname">{s.name}</span>
                                                    <span className="sc-reason">{s.reason}</span>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="sc-indicators">
                                            {d.indicators.rsi != null && <span>RSI {d.indicators.rsi}</span>}
                                            {d.indicators.macd?.histogram != null &&
                                                <span>MACD hist {d.indicators.macd.histogram > 0 ? '+' : ''}{d.indicators.macd.histogram}</span>}
                                            {d.indicators.stochastic &&
                                                <span>Stoch %K {d.indicators.stochastic.k}</span>}
                                            {d.indicators.fundingRate != null &&
                                                <span>Funding {(d.indicators.fundingRate * 100).toFixed(4)}%</span>}
                                        </div>
                                    </div>
                                )}

                                {/* Apply to trade — only for actionable signals */}
                                {d.signal !== 'HOLD' && (
                                    <button className="sc-apply-btn"
                                        style={{ borderColor: color, color: color }}
                                        onClick={() => handleApply(sym, d)}>
                                        ➤ Apply to Manual Trade
                                    </button>
                                )}
                            </>)}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SignalPanel;
